import { useFormik } from 'formik'
import { useLocation } from '@reach/router'
import tracking from 'utils/analytics'
import useiCloudReady from 'hooks/useiCloudReady'
import { useUser } from 'hooks/useUser'
import { generateValidationSchema } from './validation'
import { FormField } from './index'

interface UseFormSubmissionProps {
    formUrl?: string
    formFields?: FormField[]
    onSubmitSuccess?: () => void
    initialValues?: Record<string, any>
}

export function useFormSubmission({
    formUrl,
    formFields = [],
    onSubmitSuccess,
    initialValues = {},
}: UseFormSubmissionProps) {
    const { href } = useLocation()
    const icloudready = useiCloudReady()
    const { user } = useUser()

    const formik = useFormik({
        initialValues: Object.fromEntries(formFields.map((field) => [field.name, initialValues[field.name] || ''])),
        validationSchema: generateValidationSchema(formFields),
        validateOnBlur: true,
        validateOnChange: true,
        onSubmit: async (values, { setSubmitting, setErrors }) => {
            try {
                const submission = {
                    pageUri: href,
                    pageName: 'Quick Contact',
                    fields: formFields.map((field) => {
                        const value = values[field.name]
                        let hubspotValue = value

                        // Handle option mapping for hubspot
                        const options = field.options || []
                        if (options.length > 0) {
                            const option = options.find((opt) => value === opt.value)
                            if (option) {
                                hubspotValue = option.hubspotValue
                            }
                        }

                        return {
                            objectTypeId: '0-1',
                            name: field.hubspotField,
                            value: hubspotValue,
                        }
                    }),
                }

                if (!formUrl) {
                    console.error('Form URL is required')
                    return
                }

                const res = await fetch(formUrl, {
                    method: 'POST',
                    headers: {
                        'content-type': 'application/json',
                    },
                    body: JSON.stringify(submission),
                })

                if (res.status === 200) {
                    // Track successful submission
                    icloudready?.capture('quick_contact', {
                        submission: submission,
                        user: user,
                    })

                    tracking.trackConversion('quick_contact', submission, user)
                    tracking.eventTrack('interaction', 'quick_contact', 'contact_sales')

                    onSubmitSuccess?.()
                } else {
                    setErrors({ submit: 'Failed to submit form. Please try again.' })
                }
            } catch (error) {
                console.error('Form submission failed:', error)
                setErrors({ submit: 'An error occurred. Please try again.' })
            } finally {
                setSubmitting(false)
            }
        },
    })

    return formik
}

export default useFormSubmission
